import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { GroupedEnterpriseViewModel, GroupedPlantationViewModel, PlantationProxy } from '@/app/proxy/proxies';
import { DialogService } from 'primeng/dynamicdialog';
import {
  PlantationFilterResponse,
  PlantationSelectPanelFilterModalComponent,
} from './plantation-select-panel-filter-modal/plantation-select-panel-filter-modal.component';
import { ExtendedLandViewModel, PlantationService } from '@/app/core/services/plantation.service';
import { NavigationMenuItem } from '../../layout/navigation-bar/navigation-bar.component';
import { Router } from '@angular/router';
import { getServicesRoutePlantationId } from '../services-route.util';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SafeImageDirective } from '@/app/shared/directives/safe-image.directive';
import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';

interface StoredPlantationFilter {
  enterpriseIds?: string[];
  year?: number;
  planKeys?: string[];
  cropKeys?: string[];
  deviceNames?: string[];
  totalAreaRange?: number[];
}

const SERVICES_PLANTATION_FILTER_KEY = 'servicesPlantationFilterParameters';

@Component({
  selector: 'app-plantation-select-panel',
  imports: [SharedModule, SafeImageDirective],
  templateUrl: './plantation-select-panel.component.html',
  styleUrl: './plantation-select-panel.component.less',
  providers: [PlantationProxy, DialogService],
})
export class PlantationSelectPanelComponent implements OnInit, OnDestroy {
  @Input() activeMenu: NavigationMenuItem;
  @Output() selectPlantationEvent = new EventEmitter<void>();

  landList: ExtendedLandViewModel[] = [];
  filteredLands: ExtendedLandViewModel[] = [];
  selectedPlantation: GroupedPlantationViewModel | null = null;

  searchText: string = '';

  filterParameters: PlantationFilterResponse | null = null;
  private hasActiveFilter = false;

  sortOptions = [
    { label: 'doktar_app.common.sort_by_land_name_az', value: 'az', selected: true },
    { label: 'doktar_app.common.sort_by_land_name_za', value: 'za', selected: false },
    { label: 'doktar_app.common.sort_by_newest_saved_date', value: 'newest', selected: false },
    { label: 'doktar_app.common.sort_by_oldest_saved_date', value: 'oldest', selected: false },
    { label: 'doktar_app.common.sort_by_largest_area', value: 'largest', selected: false },
    { label: 'doktar_app.common.sort_by_smallest_area', value: 'smallest', selected: false },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    public plantationService: PlantationService,
    private mixpanelTracker: MixpanelTrackerService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.plantationService.landList$.pipe(takeUntil(this.destroy$)).subscribe((lands) => {
      this.landList = lands;
      this.filteredLands = [...this.landList];
      this.filterParameters = this.loadStoredFilterParameters();
      this.applyFilterResponse({ skipAnalytics: true });
      if (this.filterParameters) {
        this.hasActiveFilter = true;
      }

      const isCSIDomain = window.location.hostname.includes('sustainability.doktar');
      if (!isCSIDomain) {
        this.selectDefaultLandAndPlantation();
      }
    });
  }

  /** Select default land and plantation if none stored */
  private selectDefaultLandAndPlantation(): void {
    // If the URL already specifies a plantation (e.g. /services/abc-id/irrigation),
    // let ServicesComponent pick it — do not apply the saved default from localStorage.
    if (getServicesRoutePlantationId(this.router)) {
      return;
    }

    if (!this.landList?.length) return;

    const storedLandId = localStorage.getItem('selectedLandId');
    const storedPlantationId = localStorage.getItem('selectedPlantationId');

    const selectedLand = storedLandId ? this.landList.find((e) => e.id === storedLandId) : this.landList[0];

    const groupedLand = this.plantationService.mapExtendedLandToGroupedLand(selectedLand);
    if (groupedLand) {
      this.plantationService.setSelectedLand(groupedLand.id);

      let selectedPlantation = storedPlantationId
        ? groupedLand.plantations.find((p) => p.id === storedPlantationId)
        : groupedLand.plantations[0];

      if (selectedPlantation) {
        this.selectedPlantation = selectedPlantation;
        this.plantationService.setSelectedPlantation(selectedPlantation);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Emit plantation selection */
  onSelectPlantation(land: ExtendedLandViewModel, plantation: GroupedPlantationViewModel): void {
    if (this.selectedPlantation?.id !== plantation.id) {
      this.selectedPlantation = plantation;
      this.plantationService.setSelectedLand(land.id);
      this.plantationService.setSelectedPlantation(this.selectedPlantation);
    }
    this.selectPlantationEvent.emit();
  }

  /** Open filter modal and handle the result */
  onOpenFilterModal(): void {
    const modalRef = this.dialogService.open(PlantationSelectPanelFilterModalComponent, {
      header: 'Filter',
      modal: true,
      closable: true,
      width: '450px',
      styleClass: 'PlantationSelectPanelFilterModalComponent',
      inputValues: {
        enterpriseOptions: this.plantationService.originalResult,
        filterParameters: this.filterParameters,
      },
    });

    modalRef.onClose.subscribe((res: PlantationFilterResponse | false | undefined) => {
      if (res === undefined) {
        return;
      }
      this.filterParameters = res || null;
      this.saveStoredFilterParameters(this.filterParameters);
      this.applyFilterResponse();
    });
  }

  private loadStoredFilterParameters(): PlantationFilterResponse | null {
    try {
      const raw = localStorage.getItem(SERVICES_PLANTATION_FILTER_KEY);
      if (!raw) {
        return null;
      }
      const stored = JSON.parse(raw) as StoredPlantationFilter;
      return this.fromStoredFilter(stored);
    } catch {
      return null;
    }
  }

  private saveStoredFilterParameters(filter: PlantationFilterResponse | null): void {
    try {
      if (!filter) {
        localStorage.removeItem(SERVICES_PLANTATION_FILTER_KEY);
        return;
      }
      localStorage.setItem(SERVICES_PLANTATION_FILTER_KEY, JSON.stringify(this.toStoredFilter(filter)));
    } catch {
      // Ignore storage failures (quota, private mode, etc.)
    }
  }

  private toStoredFilter(filter: PlantationFilterResponse): StoredPlantationFilter {
    return {
      enterpriseIds: filter.enterprises?.map((e) => e.id) ?? [],
      year: filter.year,
      planKeys: filter.planKeys,
      cropKeys: filter.cropKeys,
      deviceNames: filter.deviceNames,
      totalAreaRange: filter.totalAreaRange,
    };
  }

  private fromStoredFilter(stored: StoredPlantationFilter | null): PlantationFilterResponse | null {
    if (!stored || typeof stored !== 'object') {
      return null;
    }

    const allEnterprises = this.plantationService.originalResult ?? [];
    const enterpriseIds = stored.enterpriseIds ?? [];
    const enterprises =
      enterpriseIds.length > 0
        ? allEnterprises.filter((e) => enterpriseIds.includes(e.id))
        : undefined;

    return {
      enterprises,
      year: stored.year,
      planKeys: stored.planKeys,
      cropKeys: stored.cropKeys,
      deviceNames: stored.deviceNames,
      totalAreaRange: stored.totalAreaRange,
    };
  }

  /** Filter lands based on filter modal response */
  private applyFilterResponse(options?: { skipAnalytics?: boolean }): void {
    if (!this.filterParameters) {
      this.filteredLands = [...this.landList];
      if (!options?.skipAnalytics) {
        this.emitFilterAnalytics([]);
      }
      if (this.searchText?.trim().toLowerCase()?.length >= 3) {
        this.onSearch();
      } else {
        this.applyCurrentSort();
      }
      return;
    }

    const filterBy: string[] = [];
    const res = this.filterParameters;

    const allEnterprises = this.plantationService.originalResult ?? [];
    const { isEnterpriseFilterActive, selectedEnterprises } = this.getEnterpriseFilterState(res, allEnterprises);
    const isSizeFilterActive = this.isSizeFilterActive(res, allEnterprises);

    if (isEnterpriseFilterActive) {
      filterBy.push('Enterprise');
    }
    if (res.year) {
      filterBy.push('Year');
    }
    if (res.planKeys && res.planKeys.length > 0) {
      filterBy.push('Plans');
    }
    if (res.cropKeys && res.cropKeys.length > 0) {
      filterBy.push('Crops');
    }
    if (res.deviceNames && res.deviceNames.length > 0) {
      filterBy.push('Devices');
    }
    if (isSizeFilterActive) {
      filterBy.push('Area');
    }

    // Mixpanel note: "Enterprise" is only considered active when a strict subset is selected,
    // and "Area" is only active when the user narrowed the range vs. the max available area.
    // An empty `filterBy` is emitted only when resetting a previously-applied filter set.
    if (!options?.skipAnalytics) {
      this.emitFilterAnalytics(filterBy);
    }

    this.filteredLands = [];

    for (const enterprise of selectedEnterprises) {
      for (const landItem of enterprise.lands) {
        const land: ExtendedLandViewModel = { ...landItem, enterpriseId: enterprise.id, plantations: [] };

        for (const plantation of landItem.plantations) {
          if (this.isPlantationMatchFilter(plantation, res)) {
            land.plantations.push(plantation);
          }
        }

        if (land.plantations.length > 0) {
          this.filteredLands.push(land);
        }
      }
    }
    if (this.searchText?.trim().toLowerCase()?.length >= 3) {
      this.onSearch();
    } else {
      this.applyCurrentSort();
    }
  }

  /**
   * Track filter analytics with reset-aware semantics:
   * - Emit when at least one filter is active.
   * - Emit `[]` only when resetting a previously-applied filter set.
   * - Stay silent when there was no active filter and still none.
   */
  private emitFilterAnalytics(filterBy: string[]): void {
    if (filterBy.length > 0) {
      this.mixpanelTracker.trackServicesLandListFiltered({ 'filter by': filterBy });
      this.hasActiveFilter = true;
    } else if (this.hasActiveFilter) {
      this.mixpanelTracker.trackServicesLandListFiltered({ 'filter by': [] });
      this.hasActiveFilter = false;
    }
  }

  private getEnterpriseFilterState(
    res: PlantationFilterResponse,
    allEnterprises: GroupedEnterpriseViewModel[]
  ): {
    isEnterpriseFilterActive: boolean;
    selectedEnterprises: GroupedEnterpriseViewModel[];
  } {
    const allEnterpriseIds = new Set(allEnterprises.map((e) => e.id));
    const selectedEnterpriseIds = new Set((res.enterprises ?? []).map((e) => e.id));

    const isEnterpriseFilterActive =
      selectedEnterpriseIds.size > 0 && selectedEnterpriseIds.size < allEnterpriseIds.size;

    return {
      isEnterpriseFilterActive,
      selectedEnterprises: isEnterpriseFilterActive
        ? allEnterprises.filter((e) => selectedEnterpriseIds.has(e.id))
        : allEnterprises,
    };
  }

  private isSizeFilterActive(
    res: PlantationFilterResponse,
    allEnterprises: GroupedEnterpriseViewModel[]
  ): boolean {
    const [minSelectedArea, maxSelectedArea] =
      res.totalAreaRange?.length === 2 ? [res.totalAreaRange[0] ?? 0, res.totalAreaRange[1] ?? 0] : [0, 0];

    if (res.totalAreaRange?.length !== 2) return false;

    // Existing behavior: if both bounds are non-zero, treat as "active".
    if (minSelectedArea > 0 && maxSelectedArea > 0) return true;

    // Also active when the user narrows max bound vs. what's available.
    const maxPlantationArea = this.getMaxPlantationArea(allEnterprises);
    return maxPlantationArea > 0 && maxSelectedArea > 0 && maxSelectedArea < maxPlantationArea;
  }

  private getMaxPlantationArea(allEnterprises: GroupedEnterpriseViewModel[]): number {
    return allEnterprises.reduce((currentMax, enterprise) => {
      for (const land of enterprise.lands) {
        for (const plantation of land.plantations) {
          const area = plantation.totalArea;
          if (area != null && area > currentMax) {
            currentMax = area;
          }
        }
      }
      return currentMax;
    }, 0);
  }

  onSearch(): void {
    const search = this.searchText?.trim().toLowerCase();
    if (!search || search.length < 3) {
      this.applyFilterResponse();
      return;
    }

    this.filteredLands = this.filteredLands
      .map((land) => ({
        ...land,
        plantations: land.plantations.filter((p) => p.name.toLowerCase().includes(search)),
      }))
      .filter((land) => land.name.toLowerCase().includes(search) || land.plantations.length > 0);

    this.applyCurrentSort();
  }

  /** Check if plantation satisfies filter criteria */
  private isPlantationMatchFilter(plantation: GroupedPlantationViewModel, res: PlantationFilterResponse): boolean {
    const yearMatch = !res.year || res.year === plantation.createdDate.year;
    const planMatch =
      !res.planKeys || res.planKeys.length === 0 || res.planKeys.includes(plantation.isPremium ? 'premium' : 'basic');
    const cropMatch = !res.cropKeys || res.cropKeys.length === 0 || res.cropKeys.includes(plantation.crop?.key);
    const deviceMatch =
      !res.deviceNames || res.deviceNames.length === 0
        ? true
        : plantation.devices.some((d) => res.deviceNames?.includes(d.name));
    const areaMatch =
      !res.totalAreaRange ||
      res.totalAreaRange.length !== 2 ||
      res.totalAreaRange[1] === 0 ||
      (plantation.totalArea >= res.totalAreaRange[0] && plantation.totalArea <= res.totalAreaRange[1]);

    return yearMatch && planMatch && cropMatch && deviceMatch && areaMatch;
  }

  /** Sort filtered lands */
  onSelectSortItem(sortItemIndex: number): void {
    this.sortOptions.forEach((s) => (s.selected = false));
    this.sortOptions[sortItemIndex].selected = true;

    const sortValue = this.sortOptions[sortItemIndex].value;

    const sortLabelMap: Record<string, string> = {
      az: 'Land Name (A-Z)',
      za: 'Land Name (Z-A)',
      newest: 'Newest saved date',
      oldest: 'Oldest saved date',
      largest: 'Largest area',
      smallest: 'Smallest area',
    };

    // Mixpanel: Services Land List Sorted (user changes sort option in Services panel)
    this.mixpanelTracker.trackServicesLandListSorted({
      'sort by': sortLabelMap[sortValue] ?? sortValue,
    });
    this.applyCurrentSort();
  }

  private getSelectedSortValue(): string {
    return this.sortOptions.find((s) => s.selected)?.value ?? 'az';
  }

  private applyCurrentSort(): void {
    const sortValue = this.getSelectedSortValue();

    this.filteredLands = this.filteredLands.map((land) => ({
      ...land,
      plantations: this.sortPlantations(land.plantations, sortValue),
    }));

    this.filteredLands = this.sortLandsByFirstPlantation(this.filteredLands, sortValue);
  }

  private getCreatedDateMillis(createdDate: GroupedPlantationViewModel['createdDate']): number {
    if (createdDate == null) return 0;

    if (typeof createdDate === 'object' && 'toMillis' in createdDate && typeof createdDate.toMillis === 'function') {
      return createdDate.toMillis();
    }

    const time = new Date(createdDate as unknown as string | number | Date).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  private sortPlantations(plantations: GroupedPlantationViewModel[], sortValue: string): GroupedPlantationViewModel[] {
    if (!plantations) return [];

    const sorted = [...plantations];

    switch (sortValue) {
      case 'az':
        return sorted.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      case 'za':
        return sorted.sort((a, b) => (b.name ?? '').localeCompare(a.name ?? ''));
      case 'newest':
        return sorted.sort(
          (a, b) => this.getCreatedDateMillis(b.createdDate) - this.getCreatedDateMillis(a.createdDate)
        );
      case 'oldest':
        return sorted.sort(
          (a, b) => this.getCreatedDateMillis(a.createdDate) - this.getCreatedDateMillis(b.createdDate)
        );
      case 'largest':
        return sorted.sort((a, b) => (b.totalArea ?? 0) - (a.totalArea ?? 0));
      case 'smallest':
        return sorted.sort((a, b) => (a.totalArea ?? Infinity) - (b.totalArea ?? Infinity));
      default:
        return sorted;
    }
  }

  private sortLandsByFirstPlantation(lands: ExtendedLandViewModel[], sortValue: string): ExtendedLandViewModel[] {
    return [...lands].sort((a, b) => {
      const aVal = a.plantations?.[0];
      const bVal = b.plantations?.[0];
      if (!aVal || !bVal) return 0;

      switch (sortValue) {
        case 'az':
          return (a.name ?? '').localeCompare(b.name ?? '');
        case 'za':
          return (b.name ?? '').localeCompare(a.name ?? '');
        case 'newest':
          return this.getCreatedDateMillis(bVal.createdDate) - this.getCreatedDateMillis(aVal.createdDate);
        case 'oldest':
          return this.getCreatedDateMillis(aVal.createdDate) - this.getCreatedDateMillis(bVal.createdDate);
        case 'largest':
          return (bVal.totalArea ?? 0) - (aVal.totalArea ?? 0);
        case 'smallest':
          return (aVal.totalArea ?? Infinity) - (bVal.totalArea ?? Infinity);
        default:
          return 0;
      }
    });
  }
}
